import isNil from 'lodash/isNil';
import {
  normalizeAppointmentWithPayments,
  normalizeAvailability,
  normalizeEvents,
  normalizeTimeBlock,
  serializeAppointmentForUpdate,
  serializeProviderAvailability,
  serializeTimeBlock,
} from 'pages/Dashboard/utils/helper';
import {
  CalendarEvent,
  CalendarEventWithPartialOptions,
  CalendarTimelineEvent,
} from 'pages/Dashboard/pages/Appointments/pages/List/types/appointment';
import {
  AppointmentDTO,
  AppointmentDetailsDTO,
  BlockedTimeSlotDTO,
  CalendarEventsResponseDTO,
  DeleteAppointmentRequestDTO,
  DeleteCalendarSlotResponseDTO,
  DoctorsAppointmentSummaryDTO,
  ProblemDetails,
  ProviderAvailabilityDTO,
  ReminderCommuniqueRequestDTO,
} from 'dtos';
import {
  normalizeDailyBreakdown,
} from 'pages/Dashboard/pages/Appointments/pages/List/utils/helper';
import {
  AppointmentForm,
} from 'pages/Dashboard/pages/Appointments/pages/List/components/Details';
import {
  sendRequest,
} from 'utils/httpClient';

export const getCalendarEventsByDateRange = (
  startDate: string,
  endDate: string,
):
  Promise<CalendarEvent[]> => (
  sendRequest<CalendarEventsResponseDTO>(
    {
      url: 'api/Encounter/GetCalendarEventsByDateRange',
      method: 'GET',
      params: {
        startDate: encodeURI(startDate),
        endDate: encodeURI(endDate),
      },
    },
  ).then(normalizeEvents)
);

export const getAppointmentsDailyBreakdown = (
  startDate: string,
  endDate: string,
):
  Promise<CalendarTimelineEvent[]> => (
  sendRequest<DoctorsAppointmentSummaryDTO[]>({
    url: 'api/Encounter/GetDailyAppointmentBreakdownByDateRange',
    method: 'GET',
    params: {
      startDate: encodeURI(startDate),
      endDate: encodeURI(endDate),
    },
  }).then(normalizeDailyBreakdown)
);

export const getAppointmentById = (id: number, signal?: AbortSignal): Promise<CalendarEvent> => (
  sendRequest<AppointmentDetailsDTO>({
    url: `api/Encounter/GetAppointmentDetailsByAppointmentId/${id}`,
    method: 'GET',
  }, signal ? { signal } : undefined)
    .then(normalizeAppointmentWithPayments)
);

export const getBlockedTimeSlotById = (id: number, signal?: AbortSignal): Promise<CalendarEvent> => (
  sendRequest<BlockedTimeSlotDTO>({
    url: 'api/BlockedTimeSlot/GetBlockedTimeSlot',
    method: 'GET',
    params: {
      blockedTimeSlotId: id,
    },
  }, signal ? { signal } : undefined).then(normalizeTimeBlock)
);

export const getProviderAvailabilitySlotById = (providerAvailabilityId: number, signal?: AbortSignal):
Promise<CalendarEvent> => (
  sendRequest<ProviderAvailabilityDTO>({
    url: 'api/ProviderAvailability/GetProviderAvailability',
    method: 'GET',
    params: {
      providerAvailabilityId,
    },
  }, signal ? { signal } : undefined).then(normalizeAvailability)
);

export const deleteAppointment = ({ data }: CalendarEventWithPartialOptions):
  Promise<AppointmentDTO> => sendRequest<DeleteAppointmentRequestDTO>({
    url: 'api/Encounter/DeleteAppointment',
    method: 'DELETE',
    data: { encounterId: data.objectId },
  });

export const sendAppointmentReminder = (data: ReminderCommuniqueRequestDTO):
  Promise<void> => sendRequest<void>({
    url: 'api/Communication/SendReminderCommunique',
    method: 'POST',
    data,
  });

export const modifyAppointment = (data: Partial<CalendarEvent>):
  Promise<AppointmentDTO> => sendRequest<AppointmentDTO>({
    url: 'api/Encounter/ModifyAppointmentDetails',
    method: 'PATCH',
    data: serializeAppointmentForUpdate(data),
  });

export const modifyBlockedTimeSlot = (data: Partial<CalendarEvent>):
  Promise<BlockedTimeSlotDTO> => sendRequest<BlockedTimeSlotDTO>({
    url: 'api/BlockedTimeSlot/AddOrUpdate',
    method: 'POST',
    data: serializeTimeBlock(data),
  });

export const deleteBlockedTimeSlotAll = (data: CalendarEvent):
  Promise<ProblemDetails> => sendRequest<ProblemDetails>({
    url: 'api/BlockedTimeSlot/Delete',
    method: 'DELETE',
    params: { blockedTimeSlotId: data.objectId },
  });

export const deleteBlockedTimeSlotPartially = ({ data, options }: CalendarEventWithPartialOptions):
Promise<DeleteCalendarSlotResponseDTO> => sendRequest<DeleteCalendarSlotResponseDTO>({
  url: 'api/BlockedTimeSlot/DeletePartially',
  method: 'POST',
  data: {
    blockedTimeSlotId: data.objectId,
    ...(options ?? {}),
  },
});

export const deleteBlockedTimeSlot = ({ data, options }: CalendarEventWithPartialOptions):
  Promise<ProblemDetails | DeleteCalendarSlotResponseDTO> => (
  !isNil(options?.deleteSingleItem)
    ? deleteBlockedTimeSlotPartially({ data, options })
    : deleteBlockedTimeSlotAll(data)
);

export const modifyAvailability = (data: Partial<CalendarEvent> & AppointmentForm):
Promise<ProviderAvailabilityDTO> => sendRequest<ProviderAvailabilityDTO>({
  url: 'api/ProviderAvailability/AddOrUpdate',
  method: 'POST',
  data: serializeProviderAvailability(data),
});

const deleteAllProviderAvailability = ({ objectId: providerAvailabilityId }: CalendarEvent):
  Promise<ProblemDetails> => sendRequest<ProblemDetails>({
    url: `api/ProviderAvailability/Delete?providerAvailabilityId=${providerAvailabilityId}`,
    method: 'DELETE',
  });

const deletePartialRecurringProviderAvailability = ({
  data,
  options,
}: CalendarEventWithPartialOptions):
  Promise<DeleteCalendarSlotResponseDTO> => sendRequest<DeleteCalendarSlotResponseDTO>({
    url: 'api/ProviderAvailability/DeletePartially',
    method: 'POST',
    data: {
      providerAvailabilityId: data?.objectId,
      ...(options ?? {}),
    },
  });

export const deleteAvailability = ({ data, options }: CalendarEventWithPartialOptions):
  Promise<ProblemDetails | DeleteCalendarSlotResponseDTO> => (
  !isNil(options?.deleteSingleItem)
    ? deletePartialRecurringProviderAvailability({ data, options })
    : deleteAllProviderAvailability(data)
);
